import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthContext } from './hooks/useAuth.js';
import { authApi } from './api/auth.js';
import type { UserDto } from './types.js';

// Pages
import { LoginPage } from './pages/LoginPage.js';
import { DashboardPage } from './pages/DashboardPage.js';
import { TemplatesPage } from './pages/TemplatesPage.js';
import { TemplateDetailPage } from './pages/TemplateDetailPage.js';
import { TemplateNewPage } from './pages/TemplateNewPage.js';
import { DocumentsPage } from './pages/DocumentsPage.js';
import { DocumentDetailPage } from './pages/DocumentDetailPage.js';
import { UsersPage } from './pages/UsersPage.js';
import { UserDetailPage } from './pages/UserDetailPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { TokensPage } from './pages/TokensPage.js';
import { OAuthSessionsPage } from './pages/OAuthSessionsPage.js';
import { Layout } from './components/Layout.js';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = React.useContext(AuthContext)!;
  if (isLoading) return <div style={{ padding: '2rem' }}>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = React.useContext(AuthContext)!;
  if (isLoading) return null;
  if (!user || user.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  const [user, setUser] = useState<UserDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    authApi.me()
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const r = await authApi.login(username, password);
    setUser(r.user);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <RequireAuth>
                <Layout />
              </RequireAuth>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="templates/new" element={<TemplateNewPage />} />
            <Route path="templates/:id" element={<TemplateDetailPage />} />
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="documents/:id" element={<DocumentDetailPage />} />
            <Route
              path="users"
              element={
                <RequireAdmin>
                  <UsersPage />
                </RequireAdmin>
              }
            />
            <Route
              path="users/:id"
              element={
                <RequireAdmin>
                  <UserDetailPage />
                </RequireAdmin>
              }
            />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/tokens" element={<TokensPage />} />
            <Route path="settings/oauth-sessions" element={<OAuthSessionsPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthContext.Provider>
  );
}
